import React, { useState } from "react";
import { Link } from "react-router-dom";
import { HeartHandshake, Send, Loader2, Lock, Ear, Eye, BookOpenText, Sparkles, Mail, Video } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";
import CoffeeBackground from "@/components/CoffeeBackground";

const REASONS = [
  {
    icon: Ear,
    title: "O relație care te consumă mai mult decât te hrănește",
    text: "Nu știi dacă să rămâi sau să pleci, dacă exagerezi sau dacă ți se pare. Punem lucrurile pe masă, fără să ținem partea nimănui, și plecăm cu un răspuns pe care îl poți folosi de mâine.",
  },
  {
    icon: Eye,
    title: "O situație de la muncă pe care n-o mai vezi limpede",
    text: "Un șef care te micșorează, un coleg care îți ia meritele, o decizie de carieră amânată de luni. Ești prea aproape ca să vezi tabloul întreg — eu îl văd de la distanță și ți-l descriu exact.",
  },
  {
    icon: Sparkles,
    title: "O prietenie care se destramă și nu înțelegi de ce",
    text: "Oamenii se îndepărtează rar din senin. Căutăm împreună ce s-a schimbat, ce e de recuperat și ce merită lăsat să plece — fără vinovăție inutilă.",
  },
  {
    icon: BookOpenText,
    title: "Un gând care nu-ți dă pace",
    text: "O alegere care revine noaptea, un tipar pe care îl repeți, o vinovăție pe care n-o mai cari. Nu diagnostic, ci claritate: ce se întâmplă de fapt și ce poți face concret.",
  },
];

const MODES = [
  { value: "email", icon: Mail, title: "În scris, pe e-mail", text: "Îmi descrii situația pe îndelete; îți răspund cu o analiză lungă, personală, în câteva zile." },
  { value: "video", icon: Video, title: "Față în față, pe video", text: "O sesiune de 50 de minute, pe Google Meet, când ne potrivim amândoi." },
];

const inputCls =
  "w-full rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] px-4 py-3 text-sm outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--tint-pink)]";

export const TalkPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [mode, setMode] = useState("email");
  const [availability, setAvailability] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || message.trim().length < 10) {
      toast.error("Am nevoie de numele, e-mailul tău și câteva rânduri.");
      return;
    }
    setSending(true);
    try {
      await api.post("/messages", { name: name.trim(), email: email.trim(), message: message.trim(), mode, availability: availability.trim() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err, "Nu am putut trimite mesajul"));
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="talk-page" className="relative overflow-hidden">
      <Seo
        title="Hai la o cafea — consiliere 1-la-1 cu Alexia, despre relații, muncă și viața de adult"
        description="Consiliere și consultanță 1-la-1 cu Alexia: relații, situații de la muncă, prietenii care se destramă, decizii amânate. În scris sau pe video. Direct, privat, fără menajamente."
      />
      <CoffeeBackground slot="talk" testId="talk-bg" />
      <div className="relative mx-auto max-w-5xl px-6 py-20">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#2B1B13]/80 px-4 py-1.5 text-xs uppercase tracking-[0.25em] text-[var(--accent-dark)]">
              <HeartHandshake className="h-3.5 w-3.5" strokeWidth={1.6} /> Hai la o cafea
            </div>
            <h1 data-testid="talk-title" className="mt-6 font-serif text-5xl font-light leading-tight text-[#F7F1E8] sm:text-6xl">
              Hai să vorbim despre ce te apasă. <span className="italic text-[var(--accent)]">Fără menajamente.</span>
            </h1>
            <p className="mt-6 text-lg font-light leading-relaxed text-[#F7F1E8]/86">
              Aici ofer consiliere 1-la-1: o relație care te consumă, o situație de la muncă pe care n-o mai vezi limpede, o
              prietenie care se destramă, o decizie pe care o tot amâni. Îmi descrii situația, eu îți spun sincer cum arată
              privită din afară și ce poți face concret — în scris, pe e-mail, sau față în față, pe video. Doar noi doi, nimic
              public.
            </p>
            <p data-testid="talk-promise" className="mt-4 text-base leading-relaxed text-[#F7F1E8]/80">
              Nu primești fraze de consolare și nici „depinde de situație”. Primești o privire lucidă, întrebările pe care nu
              ți le pune nimeni și un plan pe care îl poți începe de mâine.
            </p>
            <p data-testid="talk-orientation" className="mt-4 rounded-2xl bg-[#2B1B13]/70 px-4 py-3 text-sm text-[#F7F1E8]/82 ring-1 ring-[color-mix(in_srgb,var(--teal)_25%,transparent)]">
              Vrei doar să întrebi ceva, anonim, fără să vorbim direct? Pentru asta e{" "}
              <Link to="/intrebari-anonime" data-testid="talk-to-questions-link" className="font-medium text-[var(--teal-dark)] underline decoration-dotted underline-offset-2">
                Cutia cu întrebări anonime
              </Link>
              .
            </p>
            <p className="mt-4 inline-flex items-center gap-2 text-sm text-[#F7F1E8]/72">
              <Lock className="h-3.5 w-3.5" strokeWidth={1.6} /> Ce-mi scrii rămâne între noi. Nu public nimic din mesajele private.
            </p>

            <div className="mt-10" data-testid="talk-reasons">
              <p className="text-xs uppercase tracking-[0.3em] text-[#F7F1E8]/68">Cu ce vin oamenii la mine</p>
              <ul className="mt-4 space-y-3">
                {REASONS.map((r, i) => (
                  <li key={r.title} data-testid={`talk-reason-${i}`} className="flex gap-4 rounded-2xl bg-[#2B1B13]/75 px-5 py-4 ring-1 ring-[color-mix(in_srgb,var(--accent)_15%,transparent)]">
                    <r.icon className="mt-0.5 h-5 w-5 shrink-0 text-[var(--accent)]" strokeWidth={1.5} />
                    <div>
                      <p className="font-serif text-lg text-[#F7F1E8]">{r.title}</p>
                      <p className="mt-1 text-sm leading-relaxed text-[#F7F1E8]/82">{r.text}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-6">
            {sent ? (
              <div data-testid="talk-sent" className="rounded-3xl bg-[#2B1B13] p-8 ring-1 ring-[color-mix(in_srgb,var(--milk)_18%,transparent)] sm:p-10">
                <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)]">Mulțumesc</p>
                <h2 className="mt-3 font-serif text-3xl font-light text-[#F7F1E8]">Am primit mesajul tău, {name.trim()}.</h2>
                <p className="mt-4 text-base leading-relaxed text-[#F7F1E8]/86">
                  Îl citesc cu atenție și îți răspund pe <strong>{email.trim()}</strong>, de obicei în câteva zile
                  {mode === "video" ? ", ca să stabilim împreună când ne vedem pe video" : ""}. Ți-am trimis și o confirmare pe e-mail.
                  Până atunci — ai făcut deja pasul cel mai greu.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="talk-form" className="space-y-4 rounded-3xl bg-[#2B1B13] p-8 ring-1 ring-[color-mix(in_srgb,var(--milk)_18%,transparent)] sm:p-10">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">Cum te cheamă</label>
                  <input data-testid="talk-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="Prenumele e suficient" className={`mt-2 ${inputCls}`} required />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">E-mailul la care îți răspund</label>
                  <input data-testid="talk-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@exemplu.ro" className={`mt-2 ${inputCls}`} required />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">Cum ai vrea să vorbim?</p>
                  <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="talk-mode-options">
                    {MODES.map((m) => (
                      <label key={m.value} data-testid={`talk-mode-${m.value}`} className={`flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 transition-colors ${mode === m.value ? "border-[var(--accent)] bg-[var(--tint-pink)]" : "border-[color-mix(in_srgb,var(--milk)_18%,transparent)] hover:bg-[#0D0907]"}`}>
                        <input type="radio" name="talk_mode" value={m.value} checked={mode === m.value} onChange={() => setMode(m.value)} className="mt-1 accent-[var(--accent)]" />
                        <span className="min-w-0">
                          <span className="flex items-center gap-1.5 text-sm font-medium text-[#F7F1E8]"><m.icon className="h-3.5 w-3.5 shrink-0 text-[var(--accent)]" strokeWidth={1.6} />{m.title}</span>
                          <span className="mt-0.5 block text-xs text-[#F7F1E8]/78">{m.text}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                {mode === "video" && (
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">Când ți-ar fi bine? (opțional)</label>
                    <input data-testid="talk-availability" value={availability} onChange={(e) => setAvailability(e.target.value)} maxLength={300} placeholder="ex.: serile de marți, weekendul dimineața" className={`mt-2 ${inputCls}`} />
                  </div>
                )}
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">Situația ta</label>
                  <textarea data-testid="talk-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={7} maxLength={4000} placeholder="Descrie situația cât de detaliat vrei: ce s-a întâmplat, ce ai încercat, ce te frământă. Nu trebuie să sune bine, trebuie doar să fie adevărat." className={`mt-2 ${inputCls} leading-relaxed`} required />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="talk-submit"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--accent)] px-6 py-3.5 text-xs font-medium uppercase tracking-[0.2em] text-white transition-transform hover:-translate-y-0.5 active:scale-95 disabled:opacity-60"
                >
                  {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.6} /> : <Send className="h-3.5 w-3.5" strokeWidth={1.6} />}
                  {sending ? "Se trimite…" : "Hai să vorbim"}
                </button>
              </form>
            )}
          </div>
        </div>
        <p data-testid="talk-disclaimer" className="mt-14 text-center text-[10px] leading-relaxed text-[#F7F1E8]/30">
          Nu sunt psiholog autorizat; discuțiile de aici nu înlocuiesc terapia sau un tratament de specialitate.
        </p>
      </div>
    </main>
  );
};

export default TalkPage;
